import { cn } from "@/lib/utils";
import { TriffinBot } from "@viable/triffin-bot";
import { motion, useReducedMotion } from "framer-motion";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { EASE_CALM, EASE_INTRO } from "../lib/motion";
import { OverlayStack } from "../shared/layout/overlay-stack";
import { WipeRevealText, wipeRevealMessageClassName } from "../shared/motion/wipe-reveal-text";
import { ChoiceCard } from "./choice-card";
import {
	PICKER_INTRO_CARD_ENTER,
	PICKER_INTRO_CHOICES,
	PICKER_INTRO_GREETING,
	PICKER_INTRO_ROBOT_CLASS,
	PICKER_INTRO_WIPE_OUT_S,
	PICKER_INTRO_WIPE_TIMING,
	type TPickerHandoffPhase,
	type TPickerIntroBeat,
	type TPickerIntroLine,
	type TPickerIntroMotion,
	pickerHandoffShowsTiles,
	pickerIntroShowsCards,
	resolvePickerIntroLineRole,
} from "./picker-intro";
import {
	PICKER_HANDOFF_LAYOUT_MODE,
	PICKER_STACK_SETTLE_DURATION_S,
	measurePickerStackTop,
} from "./picker-stack-motion";
import { QUESTIONNAIRE_CHOICES } from "./questionnaire-choices";
import { JOURNEY_STEP_ID, type TQuestionnairePath } from "./questionnaire-flow";
import {
	QUESTIONNAIRE_OPTIONS_CLUSTER_CLASS,
	QUESTIONNAIRE_TRANSCRIPT_OPTIONS_CLASS,
	QUESTIONNAIRE_TRANSCRIPT_ROW_BASE_CLASS,
	questionnaireOptionsGridClass,
} from "./questionnaire-option-layout";

const ROBOT_REVEAL_BLUR_PX = 16;
const CARD_LAYOUT_TRANSITION = { layout: { duration: 0.55, ease: EASE_CALM } };

const INTRO_LINES: Record<TPickerIntroLine, ReactNode> = {
	greeting: PICKER_INTRO_GREETING,
	pitch: (
		<>
			See how I can help you revolutionise <br className="max-lg:hidden" />
			your business finance operations...
		</>
	),
	choices: PICKER_INTRO_CHOICES,
};

function IntroLine({
	variant,
	beat,
	motionMode,
}: {
	variant: TPickerIntroLine;
	beat: TPickerIntroBeat;
	motionMode: TPickerIntroMotion;
}) {
	const timing = PICKER_INTRO_WIPE_TIMING[variant];
	return (
		<WipeRevealText
			as={variant === "choices" ? "h1" : "h2"}
			role={resolvePickerIntroLineRole(variant, beat, motionMode)}
			className={wipeRevealMessageClassName(variant === "choices")}
			wipeInDuration={timing.duration}
			wipeInDelay={timing.delay}
			wipeOutDuration={PICKER_INTRO_WIPE_OUT_S}
		>
			{INTRO_LINES[variant]}
		</WipeRevealText>
	);
}

type TQuestionnairePickerProps = {
	beat: TPickerIntroBeat;
	motionMode: TPickerIntroMotion;
	onSelect: (path: TQuestionnairePath) => void;
	selectedPath?: TQuestionnairePath | null;
	handoffPhase?: TPickerHandoffPhase;
};

/** Picker intro + first-click handoff. The same ChoiceCards stay mounted through the transition. */
export function QuestionnairePicker({
	beat,
	motionMode,
	onSelect,
	selectedPath = null,
	handoffPhase = "idle",
}: TQuestionnairePickerProps) {
	const reducedMotion = useReducedMotion();
	const reducedMotionEnabled = Boolean(reducedMotion);
	const skipIntroMotion = reducedMotionEnabled || motionMode === "skip";
	const cardsVisible = pickerIntroShowsCards(beat);
	const committed = selectedPath !== null;
	const settled = committed && pickerHandoffShowsTiles(handoffPhase);
	const inTranscriptSlot = committed && pickerHandoffShowsTiles(handoffPhase);
	const showCards = committed ? handoffPhase !== "idle" : cardsVisible;
	const showSelectedState = committed;
	const headerRef = useRef<HTMLDivElement>(null);
	const [stackTop, setStackTop] = useState(0);

	useLayoutEffect(() => {
		const header = headerRef.current;
		if (!header) return;
		const update = () => {
			setStackTop(
				measurePickerStackTop({
					headerHeight: header.getBoundingClientRect().height,
					cardsVisible: cardsVisible || committed,
					settled,
				}),
			);
		};
		const observer = new ResizeObserver(update);
		observer.observe(header);
		update();
		window.addEventListener("resize", update);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", update);
		};
	}, [cardsVisible, committed, settled]);

	return (
		<div
			className={cn("relative w-full font-sans text-teal-500", committed ? "flex-none" : "flex-1")}
			data-questionnaire-picker-handoff={handoffPhase}
			data-questionnaire-picker-settled={settled ? "" : undefined}
		>
			<motion.div
				className="w-full"
				initial={false}
				animate={{ paddingTop: stackTop }}
				transition={{
					duration: reducedMotionEnabled ? 0 : settled ? PICKER_STACK_SETTLE_DURATION_S : 1,
					ease: EASE_INTRO,
				}}
			>
				<div
					className={cn(QUESTIONNAIRE_TRANSCRIPT_ROW_BASE_CLASS, "gap-10 md:gap-12")}
					data-questionnaire-turn={JOURNEY_STEP_ID}
					data-questionnaire-turn-current={committed ? "" : undefined}
				>
					<div ref={headerRef} className="flex w-full flex-col items-center gap-0 text-center">
						<motion.div
							className={cn("pointer-events-none", PICKER_INTRO_ROBOT_CLASS)}
							initial={
								skipIntroMotion
									? { opacity: 1, filter: "blur(0px)" }
									: { opacity: 0, filter: `blur(${ROBOT_REVEAL_BLUR_PX}px)` }
							}
							animate={{ opacity: 1, filter: "blur(0px)" }}
							transition={
								skipIntroMotion
									? { duration: 0 }
									: {
											opacity: { duration: 0.72, ease: EASE_INTRO },
											filter: { duration: 0.72, ease: EASE_INTRO },
										}
							}
						>
							<TriffinBot className="h-full w-full" fit="full" shouldCycleSleepWake={false} />
						</motion.div>
						<OverlayStack className="w-[min(640px,100%)] max-lg:w-[min(24rem,100%)]">
							<IntroLine variant="greeting" beat={beat} motionMode={motionMode} />
							<IntroLine variant="pitch" beat={beat} motionMode={motionMode} />
							<IntroLine variant="choices" beat={beat} motionMode={motionMode} />
						</OverlayStack>
					</div>
					<motion.div
						layout={PICKER_HANDOFF_LAYOUT_MODE}
						transition={CARD_LAYOUT_TRANSITION}
						className={cn(
							QUESTIONNAIRE_TRANSCRIPT_OPTIONS_CLASS,
							inTranscriptSlot ? "justify-end" : "justify-center",
							!showCards && "pointer-events-none",
						)}
						aria-hidden={!showCards}
					>
						<motion.div
							layout={PICKER_HANDOFF_LAYOUT_MODE}
							transition={CARD_LAYOUT_TRANSITION}
							className={cn(
								inTranscriptSlot
									? QUESTIONNAIRE_OPTIONS_CLUSTER_CLASS
									: "w-[min(1026px,calc(100%-32px))] max-lg:w-[min(300px,calc(100%-90px))]",
							)}
						>
							<div
								className={cn(
									inTranscriptSlot
										? questionnaireOptionsGridClass(QUESTIONNAIRE_CHOICES.length)
										: "grid w-full grid-cols-3 items-stretch gap-4 max-lg:grid-cols-1 max-lg:gap-3",
								)}
								data-questionnaire-options=""
								data-questionnaire-step={JOURNEY_STEP_ID}
								aria-hidden={!showCards}
							>
								{QUESTIONNAIRE_CHOICES.map((choice, index) => (
									<ChoiceCard
										key={choice.path}
										layoutId={`journey-choice-${choice.path}`}
										choice={choice}
										compact={inTranscriptSlot}
										onSelect={onSelect}
										selected={showSelectedState && choice.path === selectedPath}
										enter={
											committed
												? {
														opacity: 1,
														y: 0,
														durationS: reducedMotionEnabled ? 0 : PICKER_STACK_SETTLE_DURATION_S,
														delayS: 0,
													}
												: {
														opacity: skipIntroMotion || cardsVisible ? 1 : 0,
														y: skipIntroMotion || cardsVisible ? 0 : PICKER_INTRO_CARD_ENTER.y,
														durationS: skipIntroMotion ? 0 : PICKER_INTRO_CARD_ENTER.durationS,
														delayS: !skipIntroMotion && cardsVisible ? index * PICKER_INTRO_CARD_ENTER.staggerS : 0,
													}
										}
									/>
								))}
							</div>
						</motion.div>
					</motion.div>
				</div>
			</motion.div>
		</div>
	);
}
